import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';

// Import all the preview components
import { DashboardPreview } from '@/components/previews/DashboardPreview';
import { QuizPreview } from '@/components/previews/QuizPreview';
import { AnalyticsPreview } from '@/components/previews/AnalyticsPreview';
import { FlashcardsPreview } from '@/components/previews/FlashcardsPreview';
import { LearningPathPreview } from '@/components/previews/LearningPathPreview';
import { StudyNotesPreview } from '@/components/previews/StudyNotesPreview';
import { CurrentAffairsPreview } from '@/components/previews/CurrentAffairsPreview';
import { MockTestPreview } from '@/components/previews/MockTestPreview';
import { AITutoringPreview } from '@/components/previews/AITutoringPreview';
import { VideoLearningPreview } from '@/components/previews/VideoLearningPreview';
import { CommunityPreview } from '@/components/previews/CommunityPreview';
import { CodePlaygroundPreview } from '@/components/previews/CodePlaygroundPreview';


interface FeatureSequenceShowcaseProps {
  embedded?: boolean;
}

const featureScreenshots = [
  {
    name: "Analytics",
    url: "/lovable-uploads/871f51dc-3381-43a8-85dc-db55c930231a.png",
    caption: "Analytics - Comprehensive performance insights",
    component: DashboardPreview
  },
  {
    name: "Analytics",
    url: "/lovable-uploads/7f46bbc1-4899-44bf-b641-c9ae0af6de27.png",
    caption: "Analytics - Track your learning progress with detailed metrics",
    component: QuizPreview
  },
  {
    name: "AI Learning Studio",
    url: "/lovable-uploads/fe6ceec4-a337-46f1-a902-95609e691b1e.png",
    caption: "AI Learning Studio - Get personalized assistance 24/7",
    component: AnalyticsPreview
  },
  {
    name: "Adaptive Learning",
    url: "/lovable-uploads/75f6ea41-d106-46c0-9b11-6f179333f0a4.png",
    caption: "Adaptive Learning - Questions that match your skill level",
    component: FlashcardsPreview
  },
  {
    name: "Adaptive Learning Path",
    url: "/lovable-uploads/a7fab99d-d9bc-41e5-8828-43df93feb683.png",
    caption: "Adaptive Learning Path - Your personalized roadmap to success",
    component: LearningPathPreview
  },
  {
    name: "Comparative Peer Analytics",
    url: "/lovable-uploads/0b50a708-946c-4b43-b88f-ab13d1ced912.png",
    caption: "Comparative Peer Analytics - See how you perform against others",
    component: StudyNotesPreview
  },
  {
    name: "Collaborative Learning",
    url: "/lovable-uploads/dec4bd77-fe47-4402-a470-61c7f7814d13.png",
    caption: "Collaborative Learning - Connect with peers for group study",
    component: CurrentAffairsPreview
  },
  {
    name: "Dashboard",
    url: "/lovable-uploads/0ba1169a-47b7-438b-887b-341cdaac6d81.png",
    caption: "Dashboard - Track your progress in one place",
    component: MockTestPreview
  },
  {
    name: "Flashcards",
    url: "/lovable-uploads/fe1e2f12-51ca-4277-84b1-340333d19e1a.png",
    caption: "Flashcards - Master concepts with spaced repetition",
    component: AITutoringPreview
  },
  {
    name: "Mock Tests",
    url: "/lovable-uploads/40370903-845a-4564-b160-2a0f15129e32.png",
    caption: "Mock Tests - Practice under exam conditions",
    component: VideoLearningPreview
  },
  {
    name: "Adaptive Learning",
    url: "/lovable-uploads/bb998b73-27d3-4993-8be1-46cd6b5a0501.png",
    caption: "Adaptive Learning - Personalized content based on your needs",
    component: CommunityPreview
  },
  {
    name: "Current Affairs", 
    url: "/lovable-uploads/33bc094b-26ce-4555-a3cb-93d8d930b297.png",
    caption: "Current Affairs - Stay updated with relevant news",
    component: CodePlaygroundPreview
  }
];
export function FeatureSequenceShowcase({ embedded = false }: FeatureSequenceShowcaseProps) 
{
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const currentFeature = featureScreenshots[currentIndex];

  useEffect(() => {
    let interval;
    if (isAutoPlaying) {
      interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % featureScreenshots.length);
        setImageLoaded(false);
        setImageError(false);
      }, 8000);
    }
    return () => interval && clearInterval(interval);
  }, [isAutoPlaying]);

  const handleSubscribe = () => {
    toast.info(`Subscribe to unlock full access to all premium features`);
    navigate('/subscription/plans');
  };

  const goToPrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev === 0 ? featureScreenshots.length - 1 : prev - 1));
    setImageLoaded(false);
    setImageError(false);
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % featureScreenshots.length);
    setImageLoaded(false);
    setImageError(false);
  };

  const handleImageLoad = () => setImageLoaded(true);
  const handleImageError = () => setImageError(true);
  const progress = ((currentIndex + 1) / featureScreenshots.length) * 100;

  return (
    <div className={cn(
  "rounded-xl border bg-white shadow-md p-4 w-full",
  embedded ? "max-w-full" : "max-w-4xl mx-auto"
)}>
      <div className="flex justify-between items-center mb-2">
        <Button size="icon" variant="ghost" onClick={goToPrevious}><ChevronLeft /></Button>
        <h2 className="text-lg font-semibold">{currentFeature.name}</h2>
        <Button size="icon" variant="ghost" onClick={goToNext}><ChevronRight /></Button>
      </div>
      <div className={cn(
  "relative overflow-hidden rounded-lg border",
  embedded ? "min-h-[240px]" : "aspect-video"
)}>

        {imageError ? (
          <div className="flex items-center justify-center h-full text-gray-500">Image failed to load</div>
        ) : (
          <img
            src={currentFeature.url}
            alt={currentFeature.caption}
            onLoad={handleImageLoad}
            onError={handleImageError}
            className={cn("w-full h-full object-cover", imageLoaded ? "opacity-100" : "opacity-0")}
          />
        )}
        {!imageLoaded && !imageError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="w-10 h-10 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
          </div>
        )}
      </div>
      <div className="mt-3 text-sm text-gray-600 text-center">
        {currentFeature.caption}
      </div>
      <Progress value={progress} className="mt-4 h-1 rounded-full" />
    </div>
  );
}